// frontend/src/pages/StatsPage.jsx
import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Paper, Grid, Divider } from '@mui/material';
import { api } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';

export default function StatsPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  async function fetchStats() {
    try {
      const response = await api.get('/data/stats'); 
      setStats(response.data);
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
    }
  }

  if (!stats) return <Typography>Cargando estadísticas...</Typography>;

  // Aseguramos que existan valores, si no existen asignamos arrays vacíos
  const topAnalitos = stats.topAnalitos || [];
  const topFuentes = stats.topFuentes || [];
  const distribucionResiduo = stats.distribucionResiduo || [];
  const promedioLmrPorAnalito = stats.promedioLmrPorAnalito || [];

  const pieDataAlimentos = [
    { name: 'Vegetales', value: stats.porcAlimentosVegetales || 0 },
    { name: 'Animales', value: stats.porcAlimentosAnimales || 0 },
    { name: 'Piensos', value: stats.porcPiensos || 0 }
  ];

  const pieResiduoData = distribucionResiduo.map(d => ({ name: d.tiporesiduo, value: d.count }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#C71585'];

  return (
    <Container sx={{ marginTop: '2rem' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', marginBottom: '2rem' }}>Estadísticas</Typography>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>Información general</Typography>
        <Typography variant="body1">Total de registros: {stats.totalRegistros || 0}</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Distribución por tipo de alimento */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p:2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Distribución por tipo de alimento</Typography>
            <PieChart width={300} height={300}>
              <Pie
                data={pieDataAlimentos}
                cx={150}
                cy={150}
                innerRadius={60}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label
              >
                {pieDataAlimentos.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </Paper>
        </Grid>

        {/* Top Analitos */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p:2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Top Analitos</Typography>
            {topAnalitos.length > 0 ? (
              <BarChart width={500} height={300} data={topAnalitos}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="analito" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#B71C1C" />
              </BarChart>
            ) : (
              <Typography>No hay datos de analitos disponibles</Typography>
            )}
          </Paper>
        </Grid>

        {/* Divider horizontal */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* Top Fuentes */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p:2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Top Fuentes</Typography>
            {topFuentes.length > 0 ? (
              <BarChart width={500} height={300} data={topFuentes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="codex_codigo" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#FFA500" />
              </BarChart>
            ) : (
              <Typography>No hay datos de fuentes disponibles</Typography>
            )}
          </Paper>
        </Grid>

        {/* Distribución por tipo de residuo */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p:2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Distribución por tipo de residuo</Typography>
            {pieResiduoData.length > 0 ? (
              <PieChart width={300} height={300}>
                <Pie
                  data={pieResiduoData}
                  cx={150}
                  cy={150}
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label
                >
                  {pieResiduoData.map((entry, index) => (
                    <Cell key={`cell-residuo-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            ) : (
              <Typography>No hay datos de residuos disponibles</Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* Promedio LMR por analito */}
        <Grid item xs={12}>
          <Paper sx={{ p:2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Promedio LMR por Analito</Typography>
            {promedioLmrPorAnalito.length > 0 ? (
              <BarChart width={600} height={300} data={promedioLmrPorAnalito}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="analito" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="promedio" fill="#008000" />
              </BarChart>
            ) : (
              <Typography>No hay datos de LMR promedio disponibles</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
